import { useCallback, useEffect, useRef } from 'react';
import type {
  AnalyticsDialogsRequest,
  AnalyticsIntervalData,
  GetClientsStatsAnalyticsStatsClientsPostApiArg,
} from '@shared/api';
import { useGetClientsStatsAnalyticsStatsClientsPostMutation } from '@shared/api';
import type { AnalyticsStatsResult } from '../lib/types';

export const useClientsStats = (
  params: AnalyticsDialogsRequest | null,
  enabled = true,
): AnalyticsStatsResult => {
  const [trigger, result] = useGetClientsStatsAnalyticsStatsClientsPostMutation();
  const lastArgsRef = useRef<GetClientsStatsAnalyticsStatsClientsPostApiArg | null>(null);

  const fetch = useCallback(
    async (p: AnalyticsDialogsRequest) => {
      const arg: GetClientsStatsAnalyticsStatsClientsPostApiArg = {
        analyticsDialogsRequest: p,
      };
      lastArgsRef.current = arg;
      try {
        await trigger(arg).unwrap();
      } catch {}
    },
    [trigger],
  );

  const refetch = useCallback(() => {
    if (!lastArgsRef.current) return;
    void trigger(lastArgsRef.current)
      .unwrap()
      .catch(() => {});
  }, [trigger]);

  useEffect(() => {
    if (!enabled || !params) return;
    void fetch(params);
  }, [enabled, params, fetch]);

  const data: AnalyticsIntervalData | undefined = result.data;

  return {
    data,
    intervals: data?.intervals ?? [],
    totalCount: data?.total_count ?? 0,
    isLoading: result.isLoading,
    isError: result.isError,
    error: result.error,
    refetch,
    reset: result.reset,
  };
};
